import React, { createContext, useContext, useState, useEffect } from 'react';
import { getResourceByAuthId, Resource } from '../lib/resources';
import { getResourceRole } from '../lib/resourceRoles';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from './BusinessContext';

interface UserPermissions {
  calendar: {
    pageAccess: boolean;
    sales?: boolean; // New permission for calendar sales summary
    viewAllBookings?: boolean; // New permission for viewing all vs only assigned bookings
    bookingInformation?: boolean; // New permission for clicking bookings to view details
    bookingSalesInfo?: boolean; // New permission for viewing sales displays and payment summary in booking details
    editBookingDetails?: boolean; // New permission for editing booking information (schedule, customer, services, etc.)
    bookingMaterials?: boolean; // New permission for viewing materials tab in booking details
    bookingRatebook?: boolean; // New permission for viewing ratebook/flat-rates tab in booking details
    bookingProfitLoss?: boolean; // New permission for viewing profit & loss tab in booking details
  };
  messages: {
    pageAccess: boolean;
  };
  crm: {
    pageAccess: boolean;
  };
  payroll: {
    pageAccess: boolean;
  };
  timeclock: {
    pageAccess: boolean;
  };
  supplies: {
    pageAccess: boolean;
  };
  services: {
    pageAccess: boolean;
  };
  resources: {
    pageAccess: boolean;
  };
  salesReports: {
    pageAccess: boolean;
  };
  teamReports: {
    pageAccess: boolean;
  };
  suppliesUsage: {
    pageAccess: boolean;
  };
  settings: {
    pageAccess: boolean;
  };
  tasks: {
    pageAccess: boolean;
  };
  bookings: {
    pageAccess: boolean;
  };
  proposals: {
    pageAccess: boolean;
  };
  customers: {
    pageAccess: boolean;
  };
  kpiDashboard: {
    pageAccess: boolean;
  };
  automations: {
    pageAccess: boolean;
  };
}

interface PermissionContextType {
  permissions: UserPermissions;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  currentResource: Resource | null;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

const defaultPermissions: UserPermissions = {
  calendar: {
    pageAccess: true, // Default to true for users without specific roles
    sales: true, // Default to true for calendar sales summary
    viewAllBookings: true, // Default to true for viewing all bookings
    bookingInformation: true, // Default to true for viewing booking details
    bookingSalesInfo: true, // Default to true for viewing sales info in booking details
    editBookingDetails: true, // Default to true for editing booking details
    bookingMaterials: true, // Default to true for viewing materials tab
    bookingRatebook: true, // Default to true for viewing ratebook tab
    bookingProfitLoss: true, // Default to true for viewing profit & loss tab
  },
  messages: {
    pageAccess: true, // Default to true for messages page access
  },
  crm: {
    pageAccess: true, // Default to true for CRM page access
  },
  payroll: {
    pageAccess: true, // Default to true for payroll page access
  },
  timeclock: {
    pageAccess: true, // Default to true for timeclock page access
  },
  supplies: {
    pageAccess: true, // Default to true for supplies page access
  },
  services: {
    pageAccess: true, // Default to true for services page access
  },
  resources: {
    pageAccess: true, // Default to true for resources page access
  },
  salesReports: {
    pageAccess: true, // Default to true for sales reports page access
  },
  teamReports: {
    pageAccess: true, // Default to true for team reports page access
  },
  suppliesUsage: {
    pageAccess: true, // Default to true for supplies usage page access
  },
  settings: {
    pageAccess: true, // Default to true for settings page access
  },
  tasks: {
    pageAccess: true, // Default to true for tasks page access
  },
  bookings: {
    pageAccess: true, // Default to true for bookings page access
  },
  proposals: {
    pageAccess: true, // Default to true for proposals page access
  },
  customers: {
    pageAccess: true, // Default to true for customers page access
  },
  kpiDashboard: {
    pageAccess: true, // Default to true for kpiDashboard page access
  },
  automations: {
    pageAccess: true, // Default to true for automations page access
  },
};

// Cache for permissions to prevent re-fetching on tab switches
const permissionsCache = new Map<string, { permissions: UserPermissions; resource: Resource | null; timestamp: number }>();
const CACHE_DURATION = 300000; // 5 minutes

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  // Check cache on initialization
  const getCachedData = () => {
    if (user && selectedBusiness?.id) {
      const cacheKey = `${user.id}-${selectedBusiness.id}`;
      const cached = permissionsCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached;
      }
    }
    return null;
  };

  const cachedData = getCachedData();
  const [permissions, setPermissions] = useState<UserPermissions>(cachedData?.permissions || defaultPermissions);
  const [loading, setLoading] = useState(!cachedData);
  const [currentResource, setCurrentResource] = useState<Resource | null>(cachedData?.resource || null);

  const loadUserPermissions = async () => {
    if (!user || !selectedBusiness?.id) {
      setPermissions(defaultPermissions);
      setLoading(false);
      return;
    }

    // Check cache first
    const cacheKey = `${user.id}-${selectedBusiness.id}`;
    const cached = permissionsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log('Using cached permissions');
      setPermissions(cached.permissions);
      setCurrentResource(cached.resource);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Get the resource record for the current user
      const resource = await getResourceByAuthId(user.id);
      
      if (!resource) {
        // User has no resource record - they are an admin
        const adminResource = {
          id: user.id,
          business_id: selectedBusiness.id,
          first_name: 'Admin',
          last_name: 'User',
          email: user.email || '',
          phone: '',
          role: 'admin',
          role_id: undefined,
          hourly_rate: 0,
          commission_rate: 0,
          status: 'active',
          last_active: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          schedule_availability: {
            monday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
            tuesday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
            wednesday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
            thursday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
            friday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
            saturday: { enabled: false, shifts: [] },
            sunday: { enabled: false, shifts: [] }
          },
          compensation_type: 'hourly' as const,
          flat_rate_amount: 0,
          commission_percentage: 0,
          earnings_ytd: 0,
          earnings_mtd: 0,
          earnings_last_month: 0,
          is_mobile_available: false,
          auth_user_id: user.id,
          is_admin: true,
          avatar_url: undefined
        };
        setCurrentResource(adminResource);
        setPermissions(defaultPermissions);
        // Cache the permissions
        const cacheKey = `${user.id}-${selectedBusiness.id}`;
        permissionsCache.set(cacheKey, {
          permissions: defaultPermissions,
          resource: adminResource,
          timestamp: Date.now()
        });
        setLoading(false);
        return;
      }

      console.log('🔍 Resource data:', {
        resourceId: resource.id,
        roleId: resource.role_id,
        oldRole: resource.role,
        hasRoleId: !!resource.role_id
      });

      if (!resource.role_id) {
        // Resource has no role assigned in new system - use defaults
        console.log('⚠️ Resource has no role_id - using old role field:', resource.role);
        setCurrentResource(resource);
        setPermissions(defaultPermissions);
        // Cache the permissions
        const cacheKey = `${user.id}-${selectedBusiness.id}`;
        permissionsCache.set(cacheKey, {
          permissions: defaultPermissions,
          resource: resource,
          timestamp: Date.now()
        });
        setLoading(false);
        return;
      }

      // Get the role and its permissions from new system
      try {
        const role = await getResourceRole(resource.role_id);
        console.log('✅ Fetched role from new system:', {
          roleId: role.id,
          roleName: role.name,
          roleDescription: role.description
        });
        
        // Add role name to the resource object for display purposes
        const resourceWithRole = {
          ...resource,
          roleName: role.name
        };
        setCurrentResource(resourceWithRole);

        if (role && role.permissions) {
          // Merge with defaults to handle missing permissions in existing roles
          const mergedPermissions = {
            ...defaultPermissions,
            ...role.permissions,
            calendar: {
              ...defaultPermissions.calendar,
              ...role.permissions.calendar,
            },
            timeclock: {
              ...defaultPermissions.timeclock,
              ...role.permissions.timeclock,
            },
            supplies: {
              ...defaultPermissions.supplies,
              ...role.permissions.supplies,
            },
            services: {
              ...defaultPermissions.services,
              ...role.permissions.services,
            },
            resources: {
              ...defaultPermissions.resources,
              ...role.permissions.resources,
            },
            salesReports: {
              ...defaultPermissions.salesReports,
              ...role.permissions.salesReports,
            },
            teamReports: {
              ...defaultPermissions.teamReports,
              ...role.permissions.teamReports,
            },
            suppliesUsage: {
              ...defaultPermissions.suppliesUsage,
              ...role.permissions.suppliesUsage,
            },
            settings: {
              ...defaultPermissions.settings,
              ...role.permissions.settings,
            },
            tasks: {
              ...defaultPermissions.tasks,
              ...role.permissions.tasks,
            },
            bookings: {
              ...defaultPermissions.bookings,
              ...role.permissions.bookings,
            },
            proposals: {
              ...defaultPermissions.proposals,
              ...role.permissions.proposals,
            },
            customers: {
              ...defaultPermissions.customers,
              ...role.permissions.customers,
            },
            kpiDashboard: {
              ...defaultPermissions.kpiDashboard,
              ...role.permissions.kpiDashboard,
            },
            automations: {
              ...defaultPermissions.automations,
              ...role.permissions.automations,
            },
          };
          console.log('🔐 Permissions loaded from new system:', {
            resourceId: resource.id,
            roleName: role.name,
            rolePermissions: role.permissions,
            mergedPermissions
          });
          setPermissions(mergedPermissions);
          // Cache the permissions
          const cacheKey = `${user.id}-${selectedBusiness.id}`;
          permissionsCache.set(cacheKey, {
            permissions: mergedPermissions,
            resource: resourceWithRole,
            timestamp: Date.now()
          });
        } else {
          console.log('🔐 No role permissions found, using defaults');
          setPermissions(defaultPermissions);
          // Cache the permissions
          const cacheKey = `${user.id}-${selectedBusiness.id}`;
          permissionsCache.set(cacheKey, {
            permissions: defaultPermissions,
            resource: resourceWithRole,
            timestamp: Date.now()
          });
        }
      } catch (roleError) {
        console.error('❌ Error fetching role from new system:', roleError);
        // Fallback to resource without role name
        setCurrentResource(resource);
        setPermissions(defaultPermissions);
      }
      
    } catch (error) {
      console.error('Error loading user permissions:', error);
      setPermissions(defaultPermissions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserPermissions();
  }, [user, selectedBusiness?.id]);

  const hasPermission = (permission: string): boolean => {
    if (loading) return false;
    
    // Admin users bypass all permission checks
    if (currentResource && currentResource.is_admin) {
      return true;
    }

    const permissionPath = permission.split('.');
    let current: any = permissions;
    
    for (const key of permissionPath) {
      if (current[key] === undefined) {
        // Special handling for optional permissions - default to true
        if (permission === 'calendar.sales' || permission === 'calendar.viewAllBookings' || permission === 'calendar.bookingInformation' || permission === 'calendar.bookingSalesInfo' || permission === 'calendar.editBookingDetails' || permission === 'calendar.bookingMaterials' || permission === 'calendar.bookingRatebook' || permission === 'calendar.bookingProfitLoss' || permission === 'calendar.bookingLiabilityWaiver' || permission === 'messages.pageAccess' || permission === 'crm.pageAccess' || permission === 'marketing.pageAccess' || permission === 'campaigns.pageAccess' || permission === 'salesMap.pageAccess' || permission === 'payroll.pageAccess' || permission === 'timeclock.pageAccess' || permission === 'timeAccounting.pageAccess' || permission === 'accountingHub.pageAccess' || permission === 'invoiceSettings.pageAccess' || permission === 'supplies.pageAccess' || permission === 'services.pageAccess' || permission === 'resources.pageAccess' || permission === 'salesReports.pageAccess' || permission === 'teamReports.pageAccess' || permission === 'suppliesUsage.pageAccess' || permission === 'settings.pageAccess' || permission === 'tasks.pageAccess' || permission === 'bookings.pageAccess' || permission === 'proposals.pageAccess' || permission === 'customers.pageAccess' || permission === 'kpiDashboard.pageAccess' || permission === 'automations.pageAccess') {
          return true;
        }
        return false;
      }
      current = current[key];
    }
    
    return current === true;
  };

  return (
    <PermissionContext.Provider 
      value={{ 
        permissions, 
        loading, 
        hasPermission, 
        currentResource 
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
} 